import { treaty } from "@elysiajs/eden";
import type { App } from "@olgen/backend";

/**
 * Einzige Brücke zwischen Frontend und Backend. Das Frontend importiert
 * ausschließlich aus diesem Paket und niemals direkt aus `@olgen/backend`
 * — hier steckt die gesamte Kenntnis über die API.
 *
 * `App` wird nur als Typ hereingezogen und verschwindet beim Bauen restlos,
 * es landet also kein Backend-Code im Bundle des Browsers.
 */
export function createApiClient(args: {
	baseUrl: string;
	/** Zusatz-Header je Request — das Frontend hängt hier die Kunden-Vorschau an. */
	headers?: () => Record<string, string>;
}) {
	return treaty<App>(args.baseUrl, {
		// Eden belebt ISO-8601-Strings standardmäßig als Date wieder. Der
		// deklarierte Typ sagt dann `string`, zur Laufzeit kommt ein Objekt an —
		// der Typecheck merkt davon nichts und React stürzt beim Rendern ab.
		// Abgeschaltet stimmen Typ und Laufzeit wieder überein.
		parseDate: false,
		headers: () => args.headers?.() ?? {},
	});
}

export type ApiClient = ReturnType<typeof createApiClient>;

// Angebotsrechner (21.09.2026) — Engine-Ergebnis, Stammdaten, Kalkulationen.
// Angebotsrechner Phase 5 (22.09.2026)
export type {
	Abrechnung,
	AenderungDto,
	Ampel,
	AnfrageDetails,
	AnfrageMeldungDto,
	AngebotArchivZeile,
	AngebotDto,
	AngebotEreignisArt,
	AngebotEreignisDto,
	AngebotInhalt,
	AngebotKontingent,
	AngebotKundenSicht,
	AngebotOeffentlich,
	AngebotStatus,
	Angebotsart,
	AngebotsVorlageDto,
	Anrede,
	AuftragArt,
	AuftragPaket,
	AuftragZweck,
	BausteinArt,
	BausteinDaten,
	BausteinDto,
	BausteinKategorie,
	BausteinRolle,
	BeitragFormat,
	BeitragStatus,
	Betreuung,
	BetreuungPhase,
	BetreuungsStufe,
	BrandAssetDto,
	Customer,
	Dienstleistung,
	DistanzErgebnis,
	Drehtag,
	DrehtagTyp,
	DrehtagTypRegel,
	EinstellungEingabe,
	EinstellungErgebnis,
	EngineErgebnis,
	FinanzenUebersicht,
	FinanzVorschlagArt,
	FinanzVorschlagStatus,
	FixkostenDaten,
	FixkostenDto,
	GespraechDto,
	HealthStatus,
	Herkunft,
	InteressentAkte,
	InteressentDto,
	InteressentStatus,
	InteressentZeile,
	KalkBausteinDto,
	KalkParameter,
	KalkulationDaten,
	KalkulationDto,
	KalkulationStatus,
	KalkulationZeile,
	Kanal,
	Katalog,
	KontingentDto,
	KontingentFormat,
	KontingentReisekosten,
	KontingentStand,
	KontingentZahlung,
	KonzeptAntwort,
	KonzeptAuftrag,
	KostensatzErgebnis,
	Kundentyp,
	KundenVerlauf,
	Land,
	Modul,
	MonatDaten,
	MonatDto,
	NummernStand,
	PaketZiel,
	PersonDaten,
	PersonDto,
	PersonRolle,
	Plattform,
	Position,
	PositionTyp,
	Protokoll,
	PruefStufe,
	Pruefung,
	ReiseErgebnis,
	ReminderDto,
	Rhythmus,
	Stammdaten,
	Stufe,
	StufenErgebnis,
	Szenario,
	TerminArt,
	TestimonialPartnerDto,
	TestimonialStatus,
	TestimonialUebersicht,
	VorlageDaten,
	VorlageDto,
	VorlageTag,
	VorschlagArt,
	VorschlagDto,
	ZaehltAls,
	Zahlungsbedingung,
	Zone,
} from "@olgen/backend";
